import { adviseOnInbreeding, describeRelatedness } from "@breenova/domain";
import { Alert, Card, RadialProgress, SegmentedControl, StatusPill } from "@breenova/ui";
import { useMemo, useState } from "react";

import { PedigreeTree } from "@/components/pedigree-tree";
import { ZoomableCanvas } from "@/components/zoomable-canvas";
import { indexPedigree, useBirdPedigree } from "@/lib/birds";

type Depth = "3" | "4" | "5";

const DEPTH_OPTIONS = [
  { value: "3" as const, label: "3 gen" },
  { value: "4" as const, label: "4 gen" },
  { value: "5" as const, label: "5 gen" },
];

/** Maps an advice band onto a status-pill tone. */
const BAND_TONES = {
  unrelated: "positive",
  acceptable: "positive",
  caution: "attention",
  avoid: "critical",
} as const;

export function PedigreeTab({ birdId }: { birdId: string }) {
  const [depth, setDepth] = useState<Depth>("3");
  const generations = Number(depth);
  const pedigree = useBirdPedigree(birdId, generations);

  const nodes = useMemo(
    () => indexPedigree(pedigree.data?.nodes ?? []),
    [pedigree.data?.nodes],
  );

  if (pedigree.isPending) {
    return <p className="p-lg text-body text-on-surface-variant">Building the pedigree…</p>;
  }

  if (pedigree.isError || !pedigree.data) {
    return (
      <div className="p-lg">
        <Alert tone="error">Could not load the pedigree.</Alert>
      </div>
    );
  }

  const { inbreedingCoefficient, completeness, filledSlots, possibleAncestors } = pedigree.data;
  const advice = adviseOnInbreeding(inbreedingCoefficient);

  return (
    <div className="flex flex-col gap-4 px-5 pb-lg md:px-gutter-wide">
      {/*
       * The coefficient as a dial rather than a number in a box. It is a proportion, and
       * a breeder reads it to answer "is this too close" — a filled arc answers that
       * before the digits are read.
       */}
      <Card className="flex items-center gap-4 p-4">
        <RadialProgress
          // Scaled against 25%, not 100%. A full-sibling mating is 25% and nothing a
          // breeder should ever see comes near 100, so a 0–100 dial would read as
          // permanently empty.
          value={Math.min(1, inbreedingCoefficient / 0.25)}
          label={`Inbreeding coefficient ${advice.percent}%`}
          strokeWidth={11}
          className="size-23 shrink-0"
        >
          <span className="text-dial text-on-surface" data-numeric>
            {advice.percent}%
          </span>
        </RadialProgress>

        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-label-caps uppercase text-on-surface-variant">Inbreeding</span>
          <StatusPill tone={BAND_TONES[advice.band]} className="w-fit">
            {advice.band}
          </StatusPill>
          <p className="text-caption text-on-surface-body">
            {describeRelatedness(inbreedingCoefficient)}
          </p>
        </div>
      </Card>

      {/* An unknown ancestor is assumed unrelated, so a sparse pedigree always
          understates inbreeding. Saying so beside a reassuring number matters. */}
      {completeness < 0.5 && inbreedingCoefficient === 0 ? (
        <Alert tone="warning">
          Only {Math.round(completeness * 100)}% of this pedigree is recorded ({filledSlots} of{" "}
          {possibleAncestors}). Unknown ancestors count as unrelated, so a coefficient of 0
          here means &ldquo;nothing known&rdquo; rather than &ldquo;nothing shared&rdquo;.
        </Alert>
      ) : null}

      <SegmentedControl
        label="Generations shown"
        value={depth}
        options={DEPTH_OPTIONS}
        onChange={setDepth}
        className="flex w-full"
      />

      <ZoomableCanvas label="pedigree" bare>
        {/* A three-generation pedigree is the bird, its parents and its grandparents: the
            bird is the first of the three, so the tree draws one fewer above it. */}
        <PedigreeTree nodes={nodes} depth={generations - 1} />
      </ZoomableCanvas>
    </div>
  );
}
